import React, { useMemo, useState } from 'react';
import {
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaDirections,
  FaPlane,
  FaRoute,
  FaCar,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { extractLocationsWithCoordinates, getDayRouteSummary, buildGoogleMapsRouteUrl } from '../utils/itineraryParser';
import { formatDistance } from '../utils/distanceUtils';

/**
 * DayDirections Component (Simplified)
 *
 * Provides Google Maps links for navigation - collapsed by default.
 * Users can expand to see the list of stops.
 */
const DayDirections = ({ content, requestId, flights = [], dayHeading = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract locations from the itinerary content
  const locations = useMemo(() => {
    return extractLocationsWithCoordinates(content || '');
  }, [content]);

  // Get estimated distance/time summary (straight-line calculations)
  const summary = useMemo(() => {
    return getDayRouteSummary(locations);
  }, [locations]);

  // Check if there are flights for this day
  const dayFlights = useMemo(() => {
    if (!flights || flights.length === 0 || !dayHeading) return [];

    // Extract date from dayHeading
    const datePatterns = [
      /(\w+)\s+(\d{1,2}),?\s+(\d{4})/,
      /(\d{4})-(\d{2})-(\d{2})/,
      /(\d{1,2})\/(\d{1,2})\/(\d{4})/
    ];

    let dayDate = null;
    for (const pattern of datePatterns) {
      const match = dayHeading.match(pattern);
      if (match) {
        try {
          if (pattern === datePatterns[0]) {
            const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
              'july', 'august', 'september', 'october', 'november', 'december'];
            const monthIndex = monthNames.indexOf(match[1].toLowerCase());
            if (monthIndex !== -1) {
              dayDate = new Date(parseInt(match[3]), monthIndex, parseInt(match[2]));
            }
          } else if (pattern === datePatterns[1]) {
            dayDate = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
          } else {
            dayDate = new Date(parseInt(match[3]), parseInt(match[1]) - 1, parseInt(match[2]));
          }
          break;
        } catch (e) {
          continue;
        }
      }
    }

    if (!dayDate) return [];
    const dayDateStr = dayDate.toISOString().split('T')[0];
    return flights.filter(flight => flight.departure_date === dayDateStr);
  }, [flights, dayHeading]);

  // Generate Google Maps URL for the full route
  // (encodes waypoints and caps them to Google's supported maximum)
  const fullRouteMapsUrl = useMemo(() => {
    if (locations.length < 2) return null;
    return buildGoogleMapsRouteUrl(locations);
  }, [locations]);

  // Generate Google Maps URL for a specific segment
  const getSegmentMapsUrl = (fromLocation, toLocation) => {
    const origin = `${fromLocation.lat},${fromLocation.lng}`;
    const destination = `${toLocation.lat},${toLocation.lng}`;
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
  };

  // If there are flights for this day, show flight info
  if (dayFlights.length > 0) {
    return (
      <div className="my-4 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 rounded-xl border-2 border-purple-200 shadow-lg overflow-hidden">
        {/* Flight Header */}
        <div className="px-4 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <FaPlane className="text-2xl" />
            </div>
            <div>
              <h4 className="font-bold text-lg">Your Flights Today</h4>
              <p className="text-sm text-white/90">{dayFlights.length} flight{dayFlights.length > 1 ? 's' : ''} scheduled</p>
            </div>
          </div>
        </div>

        {/* Flight Details */}
        <div className="p-4 space-y-4">
          {dayFlights.map((flight, index) => (
            <div key={flight.id || index} className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-bold">
                    {flight.airline || 'Flight'} {flight.flight_number || ''}
                  </span>
                  {flight.flight_type && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      flight.flight_type === 'outbound' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {flight.flight_type === 'outbound' ? 'Outbound' : 'Return'}
                    </span>
                  )}
                </div>
                {flight.confirmation_code && (
                  <span className="text-xs text-gray-500">Conf: {flight.confirmation_code}</span>
                )}
              </div>

              <div className="flex items-center justify-between">
                {/* Departure */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{flight.departure_airport_iata}</div>
                  <div className="text-sm text-gray-600">{flight.departure_city || flight.departure_airport_name}</div>
                  <div className="text-lg font-semibold text-purple-700 mt-1">{flight.departure_time}</div>
                </div>

                {/* Flight Path Indicator */}
                <div className="flex-1 flex items-center justify-center px-4">
                  <div className="flex items-center w-full max-w-xs">
                    <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-purple-400 to-indigo-400 relative">
                      <FaPlane className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-purple-600 text-lg" />
                    </div>
                    <div className="w-3 h-3 rounded-full bg-indigo-400"></div>
                  </div>
                </div>

                {/* Arrival */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{flight.arrival_airport_iata}</div>
                  <div className="text-sm text-gray-600">{flight.arrival_city || flight.arrival_airport_name}</div>
                  <div className="text-lg font-semibold text-indigo-700 mt-1">{flight.arrival_time}</div>
                  {flight.arrival_date !== flight.departure_date && (
                    <div className="text-xs text-orange-600 font-medium">+1 day</div>
                  )}
                </div>
              </div>

              {/* Terminal Info */}
              {(flight.departure_terminal || flight.arrival_terminal) && (
                <div className="flex justify-between mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  {flight.departure_terminal && <span>Depart: Terminal {flight.departure_terminal}</span>}
                  {flight.arrival_terminal && <span>Arrive: Terminal {flight.arrival_terminal}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // If not enough locations for directions
  if (locations.length < 2) {
    return null; // Don't show anything if there are no directions to show
  }

  return (
    <div className="my-4 bg-gradient-to-r from-blue-50 via-sky-50 to-cyan-50 rounded-xl border border-blue-200 shadow-md overflow-hidden">
      {/* Compact Header - Always visible */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <FaDirections className="text-xl flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-semibold">
                {locations.length} stops · ~{formatDistance(summary.estimatedDistance)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Google Maps Link */}
            {fullRouteMapsUrl && (
              <a
                href={fullRouteMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg font-semibold transition-colors flex-shrink-0 shadow-sm text-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <FaCar />
                <span className="hidden sm:inline">Google Maps</span>
                <FaExternalLinkAlt size={10} />
              </a>
            )}

            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg font-medium transition-colors text-sm"
            >
              <span className="hidden sm:inline">{isExpanded ? 'Hide' : 'Show'} Stops</span>
              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </button>
          </div>
        </div>
      </div>

      {/* Expandable Stops List */}
      {isExpanded && (
        <div className="p-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <FaRoute className="text-blue-500" />
              <span className="font-semibold text-gray-700">Today's Stops</span>
            </div>

            <div className="space-y-3">
              {locations.map((location, index) => (
                <div key={index} className="flex items-start gap-3">
                  {/* Stop Number */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm ${
                    index === 0 ? 'bg-green-500' :
                    index === locations.length - 1 ? 'bg-red-500' :
                    'bg-blue-500'
                  }`}>
                    {index + 1}
                  </div>

                  {/* Location Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt className={`flex-shrink-0 ${
                        index === 0 ? 'text-green-500' :
                        index === locations.length - 1 ? 'text-red-500' :
                        'text-blue-500'
                      }`} />
                      <span className="font-medium text-gray-800 truncate">{location.name}</span>
                    </div>

                    {/* Segment link (except for last location) */}
                    {index < locations.length - 1 && (
                      <div className="mt-2 ml-6 flex items-center gap-2 text-sm">
                        <div className="border-l-2 border-dashed border-gray-300 h-4"></div>
                        <a
                          href={getSegmentMapsUrl(location, locations[index + 1])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                        >
                          <span>Directions to next stop</span>
                          <FaExternalLinkAlt size={10} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayDirections;
